import { defineStore } from 'pinia';
import CryptoJS from 'crypto-js';

const SECRET_KEY = process.env.VITE_COOKIE_SECRET || "";
const COOKIE_HTTP_ONLY = !process.dev;
const COOKIE_SECURE = !process.dev;
const getCookieMaxAge = () => {
  const runtimeConfig = useRuntimeConfig();
  return runtimeConfig.public.CookieMaxAge;
};
const serializer = {
  serialize: (value: any): string => {
    const json = JSON.stringify(value);
    const encrypted = CryptoJS.AES.encrypt(json, SECRET_KEY).toString();
    return encrypted;
  },
  deserialize: (value: string): any => {
    const bytes = CryptoJS.AES.decrypt(value, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  },
};
const getCookieOptions = () => ({
  maxAge: getCookieMaxAge(),
  secure: COOKIE_SECURE,
  httpOnly: COOKIE_HTTP_ONLY,
  sameSite: 'strict' as const
});

const getCookieStorage = () =>
  piniaPluginPersistedstate.cookies({
    maxAge: getCookieMaxAge(),
    secure: COOKIE_SECURE,
    httpOnly: COOKIE_HTTP_ONLY,
    sameSite: 'strict'
  });

const storage = {
  getItem: (key: string) => {
    if (process.client && COOKIE_HTTP_ONLY) {
      return null;
    }
    return getCookieStorage().getItem(key);
  },
  setItem: (key: string, value: string) => {
    if (process.client && COOKIE_HTTP_ONLY) {
      void fetch('/api/persist-cookie', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value, options: getCookieOptions() })
      }).catch((error) => {
        console.error('Failed to persist auth cookie', error);
      });
      return;
    }
    getCookieStorage().setItem(key, value);
  },
  removeItem: (key: string) => {
    if (process.client && COOKIE_HTTP_ONLY) {
      void fetch('/api/persist-cookie', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key })
      }).catch((error) => {
        console.error('Failed to remove auth cookie', error);
      });
      return;
    }
    getCookieStorage().removeItem(key);
  }
};

const hydrateAuthFromApi = (context: { store: any }) => {
  if (!process.client || !COOKIE_HTTP_ONLY) return;
  const key = context.store.$id;
  void (async () => {
    const { value } = await $fetch<{ value?: string }>('/api/persist-cookie', {
      method: 'GET',
      query: { key }
    }).catch(() => ({ value: undefined }));
    if (!value) return;
    try {
      const deserialized = serializer.deserialize(value);
      context.store.$patch(deserialized);
    } catch (error) {
      console.error('Failed to hydrate auth cookie', error);
    }
  })();
};
export const useAuth = defineStore('auth', {
  state: (): Auth => ({
    access_token: null,
    user: {
      id: null,
      first_name: null,
      last_name: null,
      phone: null,
      email: null,
      partner: null,
    },
  }),
  getters: {
    isAuthenticated: (state): boolean => {
      return !!(state.access_token && state.user);
    }
  },
  actions: {
    setAccessToken(token: string | null) {
      this.access_token = token;
    },
    async setUser(data: User) {
      this.user = data;
      const reward = useReward();
      if (reward.enabled && reward.provider) {
        await reward.$reset();
        await reward.initialize()
      }

      const analytics = useAnalytics();
      analytics.setUserProfile({
        id: data?.id ? String(data.id) : '',
        name: data?.first_name ?? undefined,
        email: data?.email ?? undefined,
        phone: data?.phone ?? undefined,
      });
    }
  },
  persist: {
    storage,
    serializer,
    afterHydrate: hydrateAuthFromApi,
  }
});
